import type { BiomarkerValue } from '@/api/generated-catch-backend/model';

interface BiomarkerReferenceRangeProps {
  resultValue: BiomarkerValue;
  tierType: string;
}

// Constants
const DOTTED_LINE_GRADIENT =
  'repeating-linear-gradient(to bottom, #6B7280 0px, #6B7280 2px, transparent 2px, transparent 4px)';

// Reusable components
function BoundaryMarker({ value }: { value: number }) {
  return (
    <div className="flex w-6 flex-col items-center justify-start gap-1 pt-0 -mt-3.5">
      <p className="text-xs font-normal leading-snug text-catch-dark-blue-90">{value}</p>
      <div className="h-8 w-0.5" style={{ background: DOTTED_LINE_GRADIENT }} />
    </div>
  );
}

function ValueMarker({ value, position }: { value: number; position: number }) {
  return (
    <div
      className="absolute top-0 flex -translate-x-1/2 flex-col items-center justify-center gap-1 -mt-3.5"
      style={{ left: `${position}%` }}
    >
      <div className="flex items-center justify-center rounded border border-catch-dark-blue-90 bg-catch-dark-blue-90 px-1.5">
        <p className="text-xs font-bold leading-snug text-white">{value}</p>
      </div>
      <div className="h-8 w-0.5 bg-catch-dark-blue-90" />
    </div>
  );
}

function ZoneBar({ color, flex }: { color: 'salmon' | 'blue' | 'green' | string; flex?: number | string }) {
  const colorClass =
    color === 'salmon'
      ? 'bg-catch-salmon'
      : color === 'blue'
        ? 'bg-catch-light-blue'
        : color === 'green'
          ? 'bg-catch-avocado-400'
          : color; // Allow custom class

  return (
    <div className="flex flex-col items-start pb-0 pt-5" style={flex ? { flex } : undefined}>
      <div className="flex w-full flex-1 flex-col rounded-sm bg-[rgba(245,159,0,0.2)]">
        <div className={`h-1.5 w-full rounded-sm ${colorClass}`} />
      </div>
    </div>
  );
}

export function BiomarkerReferenceRange({ resultValue, tierType }: BiomarkerReferenceRangeProps) {
  const { referenceRangeMin, referenceRangeMax, numericValue, optimalRangeMin, optimalRangeMax } = resultValue;

  // 1 Tier biomarkers don't have range visualizations (present/absent findings)
  if (tierType === '1 Tier') {
    return null;
  }

  // If we don't have the necessary data for visualization, don't render
  if (referenceRangeMin == null || referenceRangeMax == null || numericValue == null) {
    return null;
  }

  // Calculate position percentage for the user's value within the reference range
  const range = referenceRangeMax - referenceRangeMin;
  const valuePositionInRange = ((numericValue - referenceRangeMin) / range) * 100;

  // Render different visualizations based on tier type
  if (tierType === '2 Tier') {
    // 2 Tier: Directional (high OR low) - two-zone visualization
    // Determine which direction is "bad" (high or low)
    const isHighBad =
      resultValue.isAboveMaxRange ||
      (resultValue.isAboveMaxRange !== false && numericValue > referenceRangeMax) ||
      (!resultValue.isBelowMinRange && referenceRangeMin <= 1);

    // Determine if normal zone should be green (when optimal = normal)
    const useGreen =
      optimalRangeMin != null &&
      optimalRangeMax != null &&
      optimalRangeMin === referenceRangeMin &&
      optimalRangeMax === referenceRangeMax;

    const normalColor = useGreen ? 'bg-catch-avocado-400' : 'bg-catch-light-blue';

    // Determine threshold value and marker position
    const threshold = isHighBad ? referenceRangeMax : referenceRangeMin;
    const thresholdValue = isHighBad ? referenceRangeMax : referenceRangeMin;

    // Calculate marker position relative to threshold
    // For high-bad: position based on distance from threshold
    // For low-bad: position based on distance from threshold
    let markerPosition: number;
    if (isHighBad) {
      // High is bad: [Normal 50%] [Threshold] [Salmon 50%]
      // Threshold is at 50%, values scale around it
      if (numericValue <= thresholdValue) {
        // In normal zone (left side)
        const normalRange = thresholdValue - referenceRangeMin;
        const positionInNormal = normalRange > 0 ? ((numericValue - referenceRangeMin) / normalRange) * 50 : 25;
        markerPosition = Math.max(5, Math.min(50, positionInNormal));
      } else {
        // In danger zone (right side)
        const dangerRange = Math.max(thresholdValue * 0.5, 10); // Scale for display
        const positionInDanger = ((numericValue - thresholdValue) / dangerRange) * 45;
        markerPosition = Math.min(95, 50 + positionInDanger);
      }
    } else {
      // Low is bad: [Salmon 50%] [Threshold] [Normal 50%]
      // Threshold is at 50%, values scale around it
      if (numericValue >= thresholdValue) {
        // In normal zone (right side)
        const normalRange = referenceRangeMax - thresholdValue;
        const positionInNormal = normalRange > 0 ? ((numericValue - thresholdValue) / normalRange) * 50 : 25;
        markerPosition = Math.max(50, Math.min(95, 50 + positionInNormal));
      } else {
        // In danger zone (left side)
        const dangerRange = Math.max(thresholdValue * 0.5, 10); // Scale for display
        const positionInDanger = (numericValue / dangerRange) * 45;
        markerPosition = Math.max(5, positionInDanger);
      }
    }

    return (
      <div className="rounded-lg bg-[#F0F5F6] px-5 py-6">
        <div className="relative flex items-start gap-0">
          {isHighBad ? (
            <>
              <ZoneBar color={normalColor} flex={1} />
              <BoundaryMarker value={threshold} />
              <ZoneBar color="salmon" flex={1} />
            </>
          ) : (
            <>
              <ZoneBar color="salmon" flex={1} />
              <BoundaryMarker value={threshold} />
              <ZoneBar color={normalColor} flex={1} />
            </>
          )}
          <ValueMarker value={numericValue} position={markerPosition} />
        </div>
      </div>
    );
  }

  // 3 Tier: Bidirectional (high AND low) - full visualization with optimal range
  // Calculate optimal zone proportions if we have optimal range data
  let leftBlueFlexUnits = 1;
  let optimalFlexUnits = 1;
  let rightBlueFlexUnits = 1;

  if (optimalRangeMin != null && optimalRangeMax != null) {
    // Calculate the proportion of the reference range occupied by each zone
    const leftBlueWidth = optimalRangeMin - referenceRangeMin;
    const optimalWidth = optimalRangeMax - optimalRangeMin;
    const rightBlueWidth = referenceRangeMax - optimalRangeMax;

    // Use the actual proportions as flex values
    // Add small epsilon to avoid zero flex values
    leftBlueFlexUnits = Math.max(leftBlueWidth / range, 0.01);
    optimalFlexUnits = Math.max(optimalWidth / range, 0.01);
    rightBlueFlexUnits = Math.max(rightBlueWidth / range, 0.01);
  }

  // Calculate salmon zones based on reference range to maintain 1:2:1 ratio (25%:50%:25%)
  // Each salmon zone should be half of the total reference range width
  const referenceRangeFlexTotal = leftBlueFlexUnits + optimalFlexUnits + rightBlueFlexUnits;
  const salmonFlexUnits = referenceRangeFlexTotal / 2;
  const totalFlexUnits = salmonFlexUnits + referenceRangeFlexTotal + salmonFlexUnits;
  const leftOffset = (salmonFlexUnits / totalFlexUnits) * 100;
  const rangeWidth = ((leftBlueFlexUnits + optimalFlexUnits + rightBlueFlexUnits) / totalFlexUnits) * 100;
  const calculatedPosition = leftOffset + (valuePositionInRange / 100) * rangeWidth;
  // Clamp to visible area (5% to 95% of container)
  const markerPosition = Math.max(5, Math.min(95, calculatedPosition));

  return (
    <div className="rounded-lg bg-[#F0F5F6] px-5 py-6">
      <div className="relative flex items-start gap-0">
        <ZoneBar color="salmon" flex={salmonFlexUnits} />
        <BoundaryMarker value={referenceRangeMin} />
        <ZoneBar color="blue" flex={leftBlueFlexUnits} />
        <div className="w-1.5" />
        <ZoneBar color="green" flex={optimalFlexUnits} />
        <div className="w-1.5" />
        <ZoneBar color="blue" flex={rightBlueFlexUnits} />
        <BoundaryMarker value={referenceRangeMax} />
        <ZoneBar color="salmon" flex={salmonFlexUnits} />
        <ValueMarker value={numericValue} position={markerPosition} />
      </div>
    </div>
  );
}
